/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import { AllowcopyPropertyThunk, CopyListPropertyThunk } from "../../../../../redux-magic/thunks";

/* Components Imports */

import { CircularProgress, Switch, Typography } from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Styled Components */

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "100%",
	gap: "0.5rem",
	padding: "1rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	borderRadius: "16px",
}));

const Heading = styled(Typography)(({ theme }) => ({
	margin: "0.5rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const SwitchContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const CopyListing = ({
	dispatch,
	session,
	loading,
	propertyData,
	profile_context,
	property_created_by,
}: {
	propertyData: LocalPropertyInterface;
	dispatch: any;
	session: any;
	loading: boolean;
	profile_context: StateInterface["profile_context"];
	property_created_by: LocalPropertyInterface["created_by"];
}) => {
	const router = React.useRef(useRouter()).current;

	const [switchLoading, setSwitchLoading] = React.useState<boolean>(false);

	const creator_user_id: string | ObjectId | UserInterface = propertyData.created_by
		? propertyData.created_by.is_business_profile
			? (propertyData.created_by.user_id as UserInterface)._id
			: (propertyData.created_by.user_id as UserInterface)._id != ""
				? (propertyData.created_by.user_id as UserInterface)._id
				: ""
		: "";

	const creator_id: ObjectId | string = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: property_created_by.user_id != ""
					? (property_created_by.user_id as UserInterface)._id
					: ""
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: ""
		: "";

	const profile_context_id: ObjectId | string = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: "";

	const is_user_creator: boolean = profile_context_id === creator_id;

	const is_creator_developer_or_landowner: boolean =
		(property_created_by.business_profile_id as BusinessProfileInterface).business_profile_type === "developer" ||
		(property_created_by.business_profile_id as BusinessProfileInterface).business_profile_type === "landowner";

	const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		setSwitchLoading(true);
		await dispatch(
			AllowcopyPropertyThunk({
				property_id: propertyData._id,
				allow_copy: event.target.checked,
			}),
		);
		setSwitchLoading(false);
	};

	const submit = async () => {
		await dispatch(
			CopyListPropertyThunk({
				property_id: propertyData._id,
				slug: propertyData.slug,
				creator_is_business_profile: propertyData.created_by.is_business_profile,
				creator_user_id: creator_user_id,
				creator_business_profile_id: creator_id,
				profile_context_is_business_profile: profile_context ? profile_context.is_business_profile : false,
				profile_context_user_id: profile_context ? profile_context.user_id : "",
				profile_context_business_profile_id: profile_context ? profile_context.business_profile_id : "",
				linked: true,
				linked_to: propertyData._id,
			}),
		);
	};

	return (
		<React.Fragment>
			{is_user_creator && is_creator_developer_or_landowner && propertyData.linked === false ? (
				<MainContainer>
					<Heading>{router.locale === "ar-AE" ? "السماح بالنسخ" : "Allow cloning"}</Heading>
					<SwitchContainer>
						<Typography variant="body2">
							{router.locale === "ar-AE"
								? "السماح للمستخدمين الآخرين بنسخ عقارك"
								: "Allow other users to clone your property"}
						</Typography>
						{switchLoading ? (
							<CircularProgress />
						) : (
							<Switch
								id="spp_allow_copy_switch"
								checked={propertyData.allow_copy}
								onChange={handleChange}
								inputProps={{ "aria-label": "controlled" }}
							/>
						)}
					</SwitchContainer>
				</MainContainer>
			) : null}

			{!is_user_creator &&
			propertyData.allow_copy === true &&
			propertyData.linked === false &&
			propertyData.published ? (
				<MainContainer>
					<Heading>{router.locale === "ar-AE" ? "نسخ هذا العقار" : "Copy this property"}</Heading>

					<BeegruButton
						id="sp_copy_copy_listing_button"
						flavor="primary"
						sx={{ width: "100%" }}
						variant="contained"
						onClick={submit}
					>
						{router.locale === "ar-AE" ? "نسخ" : "Copy"}
					</BeegruButton>
				</MainContainer>
			) : null}
		</React.Fragment>
	);
};

export default CopyListing;
